import React, { useCallback, useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import style from "./Monetization.module.css";
import { showToast } from "../../utils/Toastify/Toastify";
import { getReferralPerformance, getWatchAnalytics, getRevenueRuns, getTvodSummary } from "../../api/HttpApis";

const money = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;
// Long creator/studio names collide when forced onto the x-axis at
// interval={0} — truncate for the axis label only; the tooltip (built from
// the same untruncated `name`) still shows the full name.
const MAX_AXIS_NAME = 14;
const truncateName = (name) =>
  name && name.length > MAX_AXIS_NAME ? `${name.slice(0, MAX_AXIS_NAME - 1)}…` : name;
const now = new Date();
const CUR_YEAR = now.getUTCFullYear();
const CUR_MONTH = now.getUTCMonth() + 1;

export default function MonetizationAnalytics() {
  const [year, setYear] = useState(CUR_YEAR);
  const [month, setMonth] = useState(CUR_MONTH);
  const [referral, setReferral] = useState([]);
  const [activeReferralCreators, setActiveReferralCreators] = useState(0);
  const [watch, setWatch] = useState(null);
  const [runs, setRuns] = useState([]);
  const [tvod, setTvod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showHow, setShowHow] = useState(false); // explainer collapsed by default

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ref, w, r, tv] = await Promise.all([
        getReferralPerformance({ year, month }).catch(() => null),
        getWatchAnalytics({ year, month }).catch(() => null),
        getRevenueRuns({ year }).catch(() => null),
        getTvodSummary({ limit: 8 }).catch(() => null),
      ]);
      // Backend envelope: { status, data: {...} }
      setReferral(ref?.data?.creators || []);
      setActiveReferralCreators(ref?.data?.active_referral_creators || 0);
      setWatch(w?.data || null);
      setRuns(r?.data?.runs || []);
      setTvod(tv?.data || null);
    } catch (err) {
      showToast("Failed to load analytics", "error");
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const topCreators = (watch?.top_creators || []).map((t) => {
    const fullName = t.creator_name || `Creator #${t.creator_user_id}`;
    return {
      name: truncateName(fullName),
      fullName,
      minutes: Number(t.watch_minutes || 0),
    };
  });

  const years = [CUR_YEAR, CUR_YEAR - 1, CUR_YEAR - 2];

  // The latest revenue run for the SELECTED period drives the pool math shown in
  // the summary cards (net revenue → pool % → per-minute value). runs come back
  // newest-first, so the first match for this year+month is the latest run.
  const latestRun =
    (runs || []).find((r) => r.period_year === year && r.period_month === month) || null;

  const netCents = Number(latestRun?.net_subscription_cents || 0);
  const poolCents = Number(latestRun?.watch_pool_cents || 0);
  const affiliateCents = Number(latestRun?.affiliate_total_cents || 0);
  const totalMinutes = Number(latestRun?.total_counted_minutes || 0);
  // The pool share is a configurable setting (MonetizationSetting.watch_pool_percent),
  // NOT a constant — this page used to hardcode "10%" in the card label and in the
  // explainer formula below, which silently misreported the platform's economics to
  // every admin whenever the setting was anything other than 10.
  //
  // Read from the run's own watch_pool_percent (the rate THAT run used), not from
  // current settings — a historical period must keep showing the rate that produced
  // its numbers after the setting changes. It is also not derived from
  // poolCents/netCents: the engine floors the pool, so that division returns 9.99%
  // for a real 10% run and 49.96% for a real 50% one. A rate is config, not a
  // quotient of rounded money.
  //
  // watch_pool_percent is a Prisma Decimal. The backend's shared serializeBigInt()
  // helper recurses into every object, which pre-empts Decimal's own (correct)
  // toJSON() and would ship the value as Decimal's internal form —
  // {"s":1,"e":1,"d":[10]} — which Number() turns into NaN, rendering a literal
  // "NaN%" here. GET /revenue-runs therefore converts this column to a plain
  // number before responding (see AdminMonetizationController.revenueRuns).
  // This reader stays strict: anything that isn't a finite number is treated as
  // unknown and the rate is omitted, never guessed and never printed as "NaN%".
  const toPercent = (raw) => {
    if (raw == null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  };
  const poolPercent = toPercent(latestRun?.watch_pool_percent);
  // Trailing zeros trimmed: 50.00 reads "50%", 12.50 reads "12.5%". Null whenever
  // the rate could not be determined — the label then omits it entirely rather
  // than ever printing "NaN%" to an admin again.
  const poolPercentLabel =
    poolPercent == null ? null : `${Number(poolPercent.toFixed(2))}%`;

  return (
    <div className={style.page}>
      <div className={style.headerRow}>
        <div>
          <h2 className={style.title}>Creator Analytics</h2>
          <p className={style.subtitle}>Referral performance, watch-time, and revenue runs.</p>
        </div>
        <div className={style.toolbar} style={{ margin: 0 }}>
          <select className={style.select} style={{ width: 110 }} value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <select className={style.select} style={{ width: 130 }} value={month} onChange={(e) => setMonth(Number(e.target.value))}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleString("en", { month: "long" })}</option>
            ))}
          </select>
        </div>
      </div>

      {loading && <div className={style.loading}>Loading...</div>}

      {/* Summary cards — the pool math at a glance, for the selected period */}
      <div className={style.cardGrid}>
        <div className={style.card}>
          <div className={style.cardLabel}>Total Subscription Revenue ({year}-{String(month).padStart(2, "0")})</div>
          <div className={style.cardValue}>{money(netCents)}</div>
          <div className={style.cardLabel} style={{ marginTop: 4 }}>net, all paid subs this month</div>
          <div className={style.cardLabel} style={{ marginTop: 4 }}>
            After Stripe's fee — lower than the charge amount shown on Subscriptions/Dashboard
          </div>
        </div>
        <div className={style.card}>
          <div className={style.cardLabel}>
            Watch Pool{poolPercentLabel ? ` (${poolPercentLabel} of revenue)` : ""}
          </div>
          <div className={style.cardValue}>{money(poolCents)}</div>
          <div className={style.cardLabel} style={{ marginTop: 4 }}>shared across all creators by minutes</div>
        </div>
        <div className={style.card}>
          <div className={style.cardLabel}>Affiliate Commissions ({year}-{String(month).padStart(2, "0")})</div>
          <div className={style.cardValue}>{money(affiliateCents)}</div>
          <div className={style.cardLabel} style={{ marginTop: 4 }}>referral payouts this month</div>
        </div>
        <div className={style.card}>
          <div className={style.cardLabel}>Total Watch Minutes</div>
          <div className={style.cardValue}>{(watch?.total_watch_minutes || 0).toLocaleString()}</div>
        </div>
        <div className={style.card}>
          <div className={style.cardLabel}>Total Views</div>
          <div className={style.cardValue}>{(watch?.total_views || 0).toLocaleString()}</div>
        </div>
        <div className={style.card}>
          <div className={style.cardLabel}>Active Referral Creators ({year}-{String(month).padStart(2, "0")})</div>
          <div className={style.cardValue}>{activeReferralCreators}</div>
          <div className={style.cardLabel} style={{ marginTop: 4 }}>
            Creators whose referral link brought in a paying subscriber this
            month — signups alone don't count, only an actual purchase.
          </div>
        </div>
        <div className={style.card}>
          <div className={style.cardLabel}>TVOD Revenue (buy/rent)</div>
          {/* tvodMetrics returns dollar fields in DOLLARS (unlike every other
              field here, which is cents) — the *100 below converts back to
              cents for money(), it is not a display bug. */}
          <div className={style.cardValue}>{money((tvod?.total_revenue || 0) * 100)}</div>
          <div className={style.cardLabel} style={{ marginTop: 4 }}>{(tvod?.total_transactions || 0).toLocaleString()} sales, all time — gross, before Stripe's fee</div>
          <div className={style.cardLabel} style={{ marginTop: 4 }}>
            {money((tvod?.settled_revenue || 0) * 100)} actually settled after Stripe's fee
            {tvod?.unsettled_stripe_transactions > 0
              ? ` (${tvod.unsettled_stripe_transactions} Stripe sale${tvod.unsettled_stripe_transactions === 1 ? "" : "s"} still awaiting this month's payout run)`
              : ""}
          </div>
        </div>
      </div>

      {/* How earnings are calculated — collapsible explainer with live numbers */}
      <div className={style.chartCard}>
        <button
          type="button"
          onClick={() => setShowHow((v) => !v)}
          style={{
            background: "none", border: "none", color: "inherit", cursor: "pointer",
            padding: 0, display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left",
          }}
        >
          <span style={{ fontSize: 13, opacity: 0.7 }}>{showHow ? "▾" : "▸"}</span>
          <h3 className={style.chartTitle} style={{ margin: 0 }}>How earnings are calculated</h3>
        </button>
        {showHow && (
          <>
            <p className={style.chartSub} style={{ marginTop: 8 }}>
              Two separate streams. {latestRun
                ? `Figures below use the latest revenue run for ${year}-${String(month).padStart(2, "0")}.`
                : `Run the revenue engine for ${year}-${String(month).padStart(2, "0")} to populate these figures.`}
            </p>
            <div style={{ display: "grid", gap: 12, fontSize: 14, lineHeight: 1.6 }}>
              <div>
                <strong>1. Affiliate commission</strong> — a fixed % of the plan a referred subscriber pays
                (e.g. 50% of a $4.99 plan = $2.50). Booked per the creator's model (1 month / 2 months / lifetime).
                Total this period: <strong>{money(affiliateCents)}</strong>.
              </div>
              <div>
                <strong>2. Watch pool</strong> — a share of subscription revenue split by watch time:
                <div style={{ marginTop: 6, fontFamily: "monospace", fontSize: 13, opacity: 0.9 }}>
                  Pool = {money(netCents)} (net revenue) × {poolPercentLabel || "—"} = <strong>{money(poolCents)}</strong><br />
                  This {money(poolCents)} pool is split across {totalMinutes.toLocaleString()} total watched minutes.<br />
                  A creator earns = their minutes ÷ {totalMinutes.toLocaleString()} × {money(poolCents)}
                </div>
              </div>
              <div style={{ opacity: 0.8 }}>
                Note: the per-minute value changes every run as total revenue and total minutes change.
                Dashboards reflect the last revenue run, not live activity.
              </div>
            </div>
          </>
        )}
      </div>

      {/* Top creators by watch minutes */}
      <div className={style.chartCard}>
        <h3 className={style.chartTitle}>Top Creators by Watch Minutes</h3>
        <p className={style.chartSub}>{year}-{String(month).padStart(2, "0")}</p>
        {topCreators.length === 0 ? (
          <div className={style.empty}>No watch data for this period.</div>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            {/* barCategoryGap/barSize keep each bar a fixed, modest width
                instead of stretching to fill its category band -- with only
                a handful of creators, an unconstrained bar fills the whole
                band and looks like an oversized block; this keeps the chart
                readable as it grows to the top 5+. */}
            <BarChart data={topCreators} barCategoryGap="30%" margin={{ bottom: 24 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis
                dataKey="name"
                stroke="#fff"
                interval={0}
                tick={{ fontSize: 12 }}
                angle={-25}
                textAnchor="end"
                height={50}
              />
              <YAxis stroke="#fff" />
              <Tooltip
                contentStyle={{ background: "#2a2a2a", border: "1px solid #444", color: "#fff" }}
                labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName || ""}
              />
              <Bar dataKey="minutes" fill="#B87333" radius={[8, 8, 0, 0]} maxBarSize={56} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* TVOD (buy/rent) — top titles by revenue */}
      <div className={style.chartCard}>
        <h3 className={style.chartTitle}>Top TVOD Titles</h3>
        <p className={style.chartSub}>Buy/rent revenue by title, all time.</p>
        <div className={`${style.tableWrap} ${style.tableScroll}`}>
          <table className={style.table}>
            <thead>
              <tr>
                <th>Title</th>
                <th>Sales</th>
                <th>Revenue</th>
              </tr>
            </thead>
            <tbody>
              {(tvod?.top_titles || []).length === 0 ? (
                <tr><td colSpan={3} className={style.empty}>No TVOD sales yet.</td></tr>
              ) : (
                tvod.top_titles.map((t) => (
                  <tr key={t.video_meta_id}>
                    <td>{t.title}</td>
                    <td>{t.count}</td>
                    <td>{money((t.revenue || 0) * 100)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Referral performance table */}
      <div className={style.chartCard}>
        <h3 className={style.chartTitle}>Referral Performance</h3>
        <p className={style.chartSub}>Clicks → signups → paying conversions per creator.</p>
        <div className={`${style.tableWrap} ${style.tableScroll}`}>
          <table className={style.table}>
            <thead>
              <tr>
                <th>Channel Name</th>
                <th>Code</th>
                <th>Clicks</th>
                <th>Signups</th>
                <th>Conversions</th>
                <th>Conversion Rate</th>
              </tr>
            </thead>
            <tbody>
              {referral.length === 0 ? (
                <tr><td colSpan={6} className={style.empty}>No referral data yet.</td></tr>
              ) : (
                referral.map((c) => (
                  <tr key={c.creator_id}>
                    <td>{c.creator_name}</td>
                    <td style={{ fontSize: 13 }}>{c.code}</td>
                    <td>{c.clicks}</td>
                    <td>{c.signups}</td>
                    <td>{c.conversions}</td>
                    <td>{c.conversion_rate}%</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Revenue runs history */}
      <div className={style.chartCard}>
        <h3 className={style.chartTitle}>Revenue Runs ({year})</h3>
        <p className={style.chartSub}>Monthly engine runs and the pool math snapshot.</p>
        <div className={`${style.tableWrap} ${style.tableScroll}`}>
          <table className={style.table}>
            <thead>
              <tr>
                <th>Period</th>
                <th>Status</th>
                <th>Net Sub Revenue</th>
                <th>Watch Pool</th>
                <th title="Eligible-only — the actual divisor used for the per-minute payout rate. A creator who isn't watch_pool_eligible doesn't count here.">
                  Payout Minutes
                </th>
                <th title="Every creator's real minutes for the period, including those not eligible for the watch pool. Will be HIGHER than Payout Minutes whenever an ineligible creator has real activity — that's expected, not an error.">
                  Platform Minutes
                </th>
                <th>Affiliate Total</th>
                <th>Creators</th>
              </tr>
            </thead>
            <tbody>
              {runs.length === 0 ? (
                <tr><td colSpan={8} className={style.empty}>No revenue runs yet.</td></tr>
              ) : (
                runs.map((r) => (
                  <tr key={r.id}>
                    <td>{r.period_year}-{String(r.period_month).padStart(2, "0")}</td>
                    <td style={{ textTransform: "capitalize" }}>{r.status}</td>
                    <td>{money(r.net_subscription_cents)}</td>
                    <td>{money(r.watch_pool_cents)}</td>
                    <td>{Number(r.total_counted_minutes || 0).toLocaleString()}</td>
                    <td>{Number(r.total_platform_counted_minutes || 0).toLocaleString()}</td>
                    <td>{money(r.affiliate_total_cents)}</td>
                    <td>{r.creators_paid}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
